import * as React from 'react';
import {useState, useEffect, useCallback, useRef} from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Textarea from '@mui/joy/Textarea';
import Input from '@mui/joy/Input';
import Divider from '@mui/joy/Divider';
import {CircularProgress} from '@mui/joy';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ReplyIcon from '@mui/icons-material/Reply';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {GetEmailMessagesForChat, PostEmailResponse, GetUser} from '../../../axios/Axios';
import {toast} from 'react-toastify';

export interface EmailMessage {
    id: string;
    subject: string;
    text_body: string;
    text_body_preview: string;
    html_body: string;
    from_email: string;
    to_email: string;
    timestamp: string;
    message_type: string;
    sender: {
        id: string;
        name: string;
        email: string;
        phone: string;
        isCustomer: boolean;
        username: string;
    };
    unread: boolean;
    tag: string;
    is_system: boolean;
}

interface EmailThreadProps {
    chatId: string;
    customerEmail?: string;
    onMarkRead?: () => void;
}

function formatEmailDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
        return date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
    } else if (diffDays === 1) {
        return 'Yesterday ' + date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
    } else if (diffDays < 7) {
        return date.toLocaleDateString('en-GB', {weekday: 'short'}) + ' ' +
            date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
    } else {
        return date.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}) + ' ' +
            date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
    }
}

function getTagLabel(tag: string): string {
    if (!tag) return '';
    return tag.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function EmailCard({email, defaultExpanded, forceExpanded, onReply}: {
    email: EmailMessage;
    defaultExpanded: boolean;
    forceExpanded?: boolean;
    onReply: (subject: string) => void;
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const isInbound = email.sender.isCustomer;

    // Respond to "Expand All" / "Collapse All" toggle
    useEffect(() => {
        if (forceExpanded !== undefined) {
            setExpanded(forceExpanded);
        }
    }, [forceExpanded]);
    const isSystem = email.is_system;

    const handleReplyClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const subj = email.subject || '';
        onReply(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
    };

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'md',
                overflow: 'hidden',
                bgcolor: '#fff',
                borderLeft: isInbound ? '4px solid' : '1px solid',
                borderLeftColor: isInbound ? 'primary.400' : 'divider',
                borderRight: !isInbound ? '4px solid' : '1px solid',
                borderRightColor: !isInbound ? (isSystem ? 'neutral.300' : 'success.400') : 'divider',
            }}
        >
            {/* Header row - always visible */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                {/* Clickable area: icon + subject + chips + timestamp + chevron */}
                <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    {/* Direction indicator */}
                    <Box sx={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isInbound ? 'primary.softBg' : (isSystem ? 'neutral.softBg' : 'success.softBg'),
                    }}>
                        {isInbound ? (
                            <PersonIcon sx={{fontSize: 16, color: 'primary.600'}}/>
                        ) : (
                            <BusinessIcon sx={{fontSize: 16, color: isSystem ? 'neutral.600' : 'success.600'}}/>
                        )}
                    </Box>

                    {/* Subject + preview */}
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{flexWrap: 'wrap'}}>
                            {(email.subject || '').toLowerCase().startsWith('re:') ? (
                                <Chip size="sm" variant="outlined" color="neutral" sx={{fontSize: '0.65rem', fontWeight: 600, minHeight: 20, px: 0.75}}>
                                    Reply
                                </Chip>
                            ) : (
                                <Chip size="sm" variant="soft" color="warning" sx={{fontSize: '0.65rem', fontWeight: 600, minHeight: 20, px: 0.75}}>
                                    New
                                </Chip>
                            )}
                            <Typography
                                level="title-sm"
                                noWrap
                                sx={{
                                    fontWeight: email.unread ? 700 : 600,
                                    color: 'text.primary',
                                    fontSize: '0.875rem',
                                }}
                            >
                                {email.subject || '(No subject)'}
                            </Typography>
                            {isSystem && email.tag && (
                                <Chip size="sm" variant="soft" color="neutral" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                                    {getTagLabel(email.tag)}
                                </Chip>
                            )}
                            {isSystem && !email.tag && (
                                <Chip size="sm" variant="soft" color="neutral" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                                    System
                                </Chip>
                            )}
                            {!isSystem && !isInbound && email.tag !== 'ai-reply' && !email.sender?.username?.endsWith('.ai') && (
                                <Chip size="sm" variant="soft" color="success" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                                    Staff
                                </Chip>
                            )}
                            {!isSystem && !isInbound && (email.tag === 'ai-reply' || email.sender?.username?.endsWith('.ai')) && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        p: '1.5px', // Border thickness
                                        background: 'linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8b00ff)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            bgcolor: '#fff',
                                            color: 'text.primary',
                                            fontSize: '0.55rem',
                                            fontWeight: 800,
                                            lineHeight: 1,
                                        }}
                                    >
                                        AI
                                    </Box>
                                </Box>
                            )}
                            {isInbound && (
                                <Chip size="sm" variant="soft" color="primary" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                                    Customer
                                </Chip>
                            )}
                        </Stack>
                        {!expanded && email.text_body_preview && (
                            <Typography level="body-sm" noWrap sx={{color: 'text.secondary', mt: 0.25}}>
                                {email.text_body_preview}
                            </Typography>
                        )}
                    </Box>

                    {/* Timestamp + expand icon */}
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{flexShrink: 0}}>
                        <Typography level="body-xs" sx={{color: 'text.secondary', whiteSpace: 'nowrap', fontWeight: 500}}>
                            {formatEmailDate(email.timestamp)}
                        </Typography>
                        {expanded ? <ExpandLessIcon sx={{fontSize: 20, color: 'text.tertiary'}}/> : <ExpandMoreIcon sx={{fontSize: 20, color: 'text.tertiary'}}/>}
                    </Stack>
                </Box>

                {/* Reply button - always visible */}
                <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<ReplyIcon sx={{fontSize: 16}}/>}
                    onClick={handleReplyClick}
                    sx={{
                        flexShrink: 0,
                        fontWeight: 600,
                        px: 1.5,
                        minHeight: 32,
                        borderWidth: '1.5px',
                        '&:hover': {
                            bgcolor: 'primary.softBg',
                            borderColor: 'primary.400',
                        },
                    }}
                >
                    Reply
                </Button>
            </Box>

            {/* Expanded body */}
            {expanded && (
                <>
                    <Divider/>
                    <Box sx={{px: 2.5, py: 2}}>
                        {/* Header details */}
                        <Stack spacing={0.5} sx={{mb: 2}}>
                            <Typography level="body-sm" sx={{color: 'text.primary'}}>
                                <Typography component="span" sx={{fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem'}}>From: </Typography>
                                {email.from_email}
                            </Typography>
                            <Typography level="body-sm" sx={{color: 'text.primary'}}>
                                <Typography component="span" sx={{fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem'}}>To: </Typography>
                                {email.to_email}
                            </Typography>
                            <Typography level="body-sm" sx={{color: 'text.primary'}}>
                                <Typography component="span" sx={{fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem'}}>Date: </Typography>
                                {new Date(email.timestamp).toLocaleString('en-GB', {
                                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                                    hour: '2-digit', minute: '2-digit',
                                })}
                            </Typography>
                            <Typography level="body-sm" sx={{color: 'text.primary'}}>
                                <Typography component="span" sx={{fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem'}}>Subject: </Typography>
                                {email.subject || '(No subject)'}
                            </Typography>
                        </Stack>

                        {/* Email body - white background for readability */}
                        <Sheet
                            variant="outlined"
                            sx={{
                                p: 2.5,
                                borderRadius: 'sm',
                                maxHeight: 450,
                                overflowY: 'auto',
                                bgcolor: 'background.surface',
                            }}
                        >
                            {email.html_body && !isSystem ? (
                                <Box
                                    sx={{
                                        '& img': {maxWidth: '100%'},
                                        '& a': {color: 'primary.500'},
                                        fontSize: '0.9rem',
                                        lineHeight: 1.7,
                                        color: 'text.primary',
                                    }}
                                    dangerouslySetInnerHTML={{__html: email.html_body}}
                                />
                            ) : (
                                <Typography
                                    level="body-md"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.7,
                                        color: 'text.primary',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {email.text_body || '(No content)'}
                                </Typography>
                            )}
                        </Sheet>
                    </Box>
                </>
            )}
        </Sheet>
    );
}

export default function EmailThread({chatId, customerEmail, onMarkRead}: EmailThreadProps) {
    const [emails, setEmails] = useState<EmailMessage[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [allExpanded, setAllExpanded] = useState<boolean | undefined>(undefined);

    // Reply composer state
    const [replySubject, setReplySubject] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [user, setUser] = useState<{username: string}>({username: ''});
    const [franchisePhone, setFranchisePhone] = useState('07488 881323');
    const [franchiseLocation, setFranchiseLocation] = useState<string | null>(null);

    const composerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = useCallback((smooth = true) => {
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, 100);
    }, []);

    const handleReplyToEmail = useCallback((subject: string) => {
        setReplySubject(subject);
        // Scroll to and focus the composer
        setTimeout(() => {
            composerRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
            const textarea = composerRef.current?.querySelector('textarea');
            textarea?.focus();
        }, 100);
    }, []);

    const fetchEmails = useCallback(async (pageNum: number, opts?: {silent?: boolean}) => {
        const silent = opts?.silent ?? false;
        if (!silent) {
            if (pageNum === 1) setIsLoading(true);
            else setIsLoadingMore(true);
        }

        try {
            const [emailResp, userResp] = await Promise.all([
                GetEmailMessagesForChat(chatId, pageNum),
                pageNum === 1 && !silent ? GetUser() : Promise.resolve(null),
            ]);

            const {messages: newMessages, hasMore: apiHasMore, franchisePhone: apiPhone, franchiseLocation: apiLocation} = emailResp.data;

            if (!silent) {
                if (apiPhone) setFranchisePhone(apiPhone);
                setFranchiseLocation(apiLocation || null);
            }

            if (silent && pageNum === 1) {
                // Polling: only update if there are new messages
                setEmails(prev => {
                    if (newMessages.length !== prev.length ||
                        (newMessages.length > 0 && prev.length > 0 &&
                         newMessages[newMessages.length - 1]?.id !== prev[prev.length - 1]?.id)) {
                        // New messages arrived — scroll to bottom
                        setTimeout(() => scrollToBottom(), 150);
                        return newMessages;
                    }
                    return prev;
                });
                setHasMore(apiHasMore);
            } else {
                setEmails(prev => pageNum === 1 ? newMessages : [...newMessages, ...prev]);
                setHasMore(apiHasMore);
                // Scroll to bottom on initial load so latest emails are visible
                if (pageNum === 1) {
                    setTimeout(() => scrollToBottom(false), 150);
                }
            }

            if (userResp) setUser(userResp.data);

            // Pre-fill reply subject from latest email
            if (pageNum === 1 && newMessages.length > 0 && !silent) {
                const latest = newMessages[newMessages.length - 1];
                const subj = latest.subject || '';
                setReplySubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
            }

            // Mark as read
            if (pageNum === 1 && onMarkRead) {
                onMarkRead();
            }
        } catch (error) {
            if (!silent) console.error('Failed to fetch email messages:', error);
        } finally {
            if (!silent) {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        }
    }, [chatId, onMarkRead, scrollToBottom]);

    // Initial load when chat changes
    useEffect(() => {
        setEmails([]);
        setPage(1);
        setHasMore(true);
        setReplySubject('');
        fetchEmails(1);
    }, [chatId]);

    // Poll for new emails every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchEmails(1, {silent: true});
        }, 10000);
        return () => clearInterval(interval);
    }, [fetchEmails]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchEmails(nextPage);
    };

    const handleSendReply = useCallback(async (subject: string, body: string) => {
        if (!body.trim() || !chatId) return;

        setIsSending(true);

        // Optimistic update
        const optimisticEmail: EmailMessage = {
            id: `temp-${Date.now()}`,
            subject,
            text_body: body,
            text_body_preview: body.substring(0, 150),
            html_body: '',
            from_email: 'office@yeticlean.co.uk',
            to_email: customerEmail || '',
            timestamp: new Date().toISOString(),
            message_type: 'response',
            sender: {
                id: '-1',
                name: user.username,
                email: 'office@yeticlean.co.uk',
                phone: '',
                isCustomer: false,
                username: user.username,
            },
            unread: false,
            tag: 'crm-reply',
            is_system: false,
        };
        setEmails(prev => [...prev, optimisticEmail]);
        scrollToBottom();

        try {
            const response = await PostEmailResponse({
                customer_id: chatId,
                subject,
                body,
            });

            if (response.data?.sent === false) {
                toast.error(response.data.message || 'Failed to send email');
                setEmails(prev => prev.filter(e => e.id !== optimisticEmail.id));
            }
        } catch (error) {
            console.error('Failed to send email:', error);
            toast.error('Failed to send email');
            setEmails(prev => prev.filter(e => e.id !== optimisticEmail.id));
        } finally {
            setIsSending(false);
        }
    }, [chatId, customerEmail, user.username, scrollToBottom]);

    if (isLoading && emails.length === 0) {
        return (
            <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <CircularProgress size="lg"/>
            </Box>
        );
    }

    if (!isLoading && emails.length === 0) {
        return (
            <>
                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, gap: 1}}>
                    <MailOutlineIcon sx={{fontSize: 48, color: 'text.tertiary'}}/>
                    <Typography level="body-lg" sx={{color: 'text.tertiary'}}>No email conversations yet</Typography>
                    <Typography level="body-sm" sx={{color: 'text.tertiary'}}>
                        Send the first email to start the conversation
                    </Typography>
                </Box>
                <div ref={composerRef}>
                    <EmailComposer
                        initialSubject={replySubject}
                        onSend={handleSendReply}
                        isSending={isSending}
                        username={user.username}
                        phone={franchisePhone}
                        location={franchiseLocation}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            {/* Email list */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    px: 2,
                    py: 2,
                    overflowY: 'auto',
                    flexDirection: 'column',
                    WebkitOverflowScrolling: 'touch',
                    bgcolor: 'neutral.softBg',
                }}
            >
                {/* Controls */}
                <Stack direction="row" spacing={1} sx={{mb: 1.5, justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography level="body-sm" sx={{color: 'text.secondary', fontWeight: 500}}>
                        {emails.length} email{emails.length !== 1 ? 's' : ''}
                    </Typography>
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={allExpanded === true ? <UnfoldLessIcon sx={{fontSize: 16}}/> : <UnfoldMoreIcon sx={{fontSize: 16}}/>}
                        onClick={() => setAllExpanded(prev => prev === true ? false : true)}
                        sx={{fontSize: 'xs'}}
                    >
                        {allExpanded === true ? 'Collapse All' : 'Expand All'}
                    </Button>
                </Stack>

                {/* Load more (older emails) */}
                {hasMore && (
                    <Box sx={{textAlign: 'center', mb: 1.5}}>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            loading={isLoadingMore}
                            onClick={handleLoadMore}
                        >
                            Load Older Emails
                        </Button>
                    </Box>
                )}

                {/* Email cards - only the LAST one is expanded by default */}
                <Stack spacing={1.5}>
                    {emails.map((email, index) => (
                        <EmailCard
                            key={email.id}
                            email={email}
                            defaultExpanded={index === emails.length - 1}
                            forceExpanded={allExpanded}
                            onReply={handleReplyToEmail}
                        />
                    ))}
                </Stack>
            </Box>

            {/* Reply composer */}
            <div ref={composerRef}>
                <EmailComposer
                    initialSubject={replySubject}
                    onSend={handleSendReply}
                    isSending={isSending}
                    username={user.username}
                    phone={franchisePhone}
                    location={franchiseLocation}
                />
            </div>
        </>
    );
}


function getFullName(username: string): string {
    if (!username) return '';
    if (username.includes('.')) {
        const parts = username.split('.');
        return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} ${parts[1].charAt(0).toUpperCase() + parts[1].slice(1)}`;
    }
    return username.charAt(0).toUpperCase() + username.slice(1);
}

const EmailComposer = React.memo(function EmailComposer({
    initialSubject,
    onSend,
    isSending,
    username,
    phone,
    location,
}: {
    initialSubject: string;
    onSend: (subject: string, body: string) => void;
    isSending: boolean;
    username: string;
    phone: string;
    location: string | null;
}) {
    const [subject, setSubject] = useState(initialSubject);
    const [body, setBody] = useState('');
    const fullName = getFullName(username);

    // Sync subject when parent provides a new initial subject (e.g. clicking Reply on an email)
    useEffect(() => {
        if (initialSubject) setSubject(initialSubject);
    }, [initialSubject]);

    const handleSend = useCallback(() => {
        if (!body.trim()) return;
        onSend(subject, body);
        setBody('');
    }, [subject, body, onSend]);

    return (
        <Box sx={{px: 2, pb: 2, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider'}}>
            <Stack spacing={1} sx={{pt: 1.5}}>
                <Input
                    size="sm"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    sx={{fontWeight: 600, fontSize: '0.875rem'}}
                    startDecorator={
                        (subject || '').toLowerCase().startsWith('re:') ? (
                            <Chip size="sm" variant="outlined" color="neutral" sx={{fontSize: '0.65rem', fontWeight: 600, minHeight: 20, px: 0.75}}>
                                Reply
                            </Chip>
                        ) : (
                            <Chip size="sm" variant="soft" color="warning" sx={{fontSize: '0.65rem', fontWeight: 600, minHeight: 20, px: 0.75}}>
                                New
                            </Chip>
                        )
                    }
                />
                <Textarea
                    placeholder="Write your email reply..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    minRows={4}
                    maxRows={12}
                    sx={{
                        fontSize: '0.875rem',
                        '& textarea:first-of-type': {
                            minHeight: 80,
                        },
                    }}
                    endDecorator={
                        <Stack
                            spacing={1.5}
                            sx={{
                                width: '100%',
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                pt: 1,
                            }}
                        >
                            {/* Signature preview */}
                            {fullName && (
                                <Box sx={{px: 1, py: 0.5, opacity: 0.7}}>
                                    <Typography
                                        level="body-sm"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            color: 'text.secondary',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {fullName}
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            mt: 0.25,
                                        }}
                                    >
                                        {phone}
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            mt: 0.25,
                                        }}
                                    >
                                        {location ? `Yeti Clean ${location}` : 'Yeti Clean'}
                                    </Typography>
                                    <Box
                                        component="img"
                                        src="https://yeticlean.co.uk/images/yeticlean-logo-transparent.png"
                                        alt="Yeti Clean"
                                        sx={{height: 24, mt: 0.5, display: 'block'}}
                                    />
                                </Box>
                            )}
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    flexGrow: 1,
                                    py: 0.5,
                                    pr: 1,
                                }}
                            >
                                <Button
                                    size="md"
                                    color="primary"
                                    endDecorator={<SendRoundedIcon/>}
                                    onClick={handleSend}
                                    loading={isSending}
                                    disabled={!body.trim()}
                                    sx={{fontWeight: 600, px: 3}}
                                >
                                    Send Email
                                </Button>
                            </Stack>
                        </Stack>
                    }
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                            handleSend();
                        }
                    }}
                />
            </Stack>
        </Box>
    );
});
